/**
 * Session lifetime helpers — the plumbing behind automatic logout.
 *
 * The failure this exists to prevent: the Entra id token expires (~60-90 min), the server
 * rejects every call with 401 (it verifies `exp`), but the SPA kept showing a signed-in UI
 * with a stale token. The app looked broken instead of simply asking the user to sign in
 * again. So a session now ends — deliberately and visibly — when any of these happen:
 *
 *   'expired'      the stored token is past `exp` and a silent refresh could not renew it
 *   'unauthorized' the API answered 401 (the server's verdict is final)
 *   'idle'         no user activity for IDLE_TIMEOUT_MS (privileged legal data)
 *
 * This module is deliberately React-free: the API client imports it too, and AuthContext
 * turns these signals into an actual logout.
 */

/** Minutes of inactivity before automatic logout. */
const IDLE_MINUTES = Number(process.env.REACT_APP_IDLE_MINUTES) || 30;
export const IDLE_TIMEOUT_MS = Math.max(1, IDLE_MINUTES) * 60 * 1000;
/** How long the "you are about to be signed out" warning shows before it happens. */
export const IDLE_WARNING_MS = 60 * 1000;
/** Renew the token when this little life is left, rather than waiting for expiry. */
export const TOKEN_REFRESH_SKEW_MS = 2 * 60 * 1000;

/** sessionStorage key: why the last session ended, so /login can explain itself. */
export const SESSION_END_REASON_KEY = 'bagent_session_end_reason';

export function decodeJwtPayload(token) {
  try {
    const part = String(token).split('.')[1];
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return {};
  }
}

/** Milliseconds until the token's `exp`; 0 when missing or unreadable (treat as expired). */
export function msUntilExpiry(token) {
  if (!token) return 0;
  const { exp } = decodeJwtPayload(token);
  if (!exp || typeof exp !== 'number') return 0;
  return exp * 1000 - Date.now();
}

export function isTokenExpired(token, skewMs = 0) {
  return msUntilExpiry(token) <= skewMs;
}

// ── Activity tracking ────────────────────────────────────────────────────────
// API calls count as activity: a deep draft polls for minutes while the user sits
// perfectly still watching the progress bar, and logging them out mid-draft would be
// its own kind of broken.

let lastActivity = Date.now();

export function markActivity() {
  lastActivity = Date.now();
}

export function msSinceActivity() {
  return Date.now() - lastActivity;
}

// ── Session-end signalling ───────────────────────────────────────────────────

const listeners = new Set();

/** Subscribe to session-end signals. Returns an unsubscribe function. */
export function onSessionEnd(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Signal that the session is over. Safe to call repeatedly (e.g. several in-flight requests
 * all 401 at once) — only the first signal for a reason propagates until it is consumed.
 */
let signalled = false;

export function notifySessionEnd(reason = 'expired') {
  if (signalled) return;
  signalled = true;
  try { sessionStorage.setItem(SESSION_END_REASON_KEY, reason); } catch { /* private mode */ }
  for (const listener of listeners) {
    try { listener(reason); } catch { /* a bad listener must not block logout */ }
  }
}

/** Called once a fresh sign-in starts, so later expiries signal again. */
export function resetSessionEndSignal() {
  signalled = false;
  try { sessionStorage.removeItem(SESSION_END_REASON_KEY); } catch { /* ignore */ }
}

/** Read and clear the reason (the login page shows it once). */
export function takeSessionEndReason() {
  try {
    const reason = sessionStorage.getItem(SESSION_END_REASON_KEY);
    if (reason) sessionStorage.removeItem(SESSION_END_REASON_KEY);
    return reason || '';
  } catch {
    return '';
  }
}
